import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { AssignmentVersionGridRow } from "../../../../services/api/types/assignments/assignment";
import { brand, gray } from "../../../../theme/themePrimitives";

interface EditVersionModalProps {
  row: AssignmentVersionGridRow | null;
  onClose: () => void;
  onSave: (
    row: AssignmentVersionGridRow,
    version: string,
    note: string,
  ) => Promise<void>;
}

export const EditVersionModal: React.FC<EditVersionModalProps> = ({
  row,
  onClose,
  onSave,
}) => {
  const [version, setVersion] = useState("");
  const [note, setNote] = useState("");
  const [versionError, setVersionError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      setVersion(row.version === "-" ? "" : row.version);
      setNote(row.note ?? "");
      setVersionError("");
    }
  }, [row]);

  const handleSave = async () => {
    if (!row) return;
    if (!version.trim()) {
      setVersionError("Version is required");
      return;
    }
    try {
      setSaving(true);
      await onSave(row, version, note);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={Boolean(row)} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "560px",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "1rem",
            borderBottom: `1px solid ${gray[300]}`,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Edit version
          </Typography>
          <IconButton sx={{ border: "none" }} onClick={onClose}>
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </Box>

        <Stack spacing={2} sx={{ marginTop: "1rem" }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Version</Typography>
            <TextField
              fullWidth
              id="edit-version"
              placeholder="Enter version"
              type="text"
              value={version}
              onChange={(e) => {
                setVersionError("");
                setVersion(e.target.value);
              }}
              variant="outlined"
              size="small"
              error={Boolean(versionError)}
              helperText={versionError}
              slotProps={{
                input: {
                  endAdornment: versionError && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
              }}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">Note</Typography>
            <TextField
              fullWidth
              id="edit-note"
              placeholder="Enter note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              variant="outlined"
              size="small"
              multiline
              minRows={4}
              sx={{
                "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-multiline":
                  {
                    padding: 0,
                  },
                "& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputMultiline":
                  {
                    padding: "8.5px 14px",
                  },
              }}
            />
          </Stack>

          <Divider />
          {saving ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Button
              variant="outlined"
              sx={{
                backgroundColor: `${brand[600]} !important`,
                color: `white`,
                "&:hover": {
                  backgroundColor: `${brand[700]} !important`,
                },
              }}
              onClick={handleSave}
            >
              Save
            </Button>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};
